/**
 * The saving of one hosted Buddy draft: the edits made in the session panel, sent to that draft
 * (`editDraft`) against the draft revision they were made on.
 *
 * ── One saver per hosted document, bound to it ───────────────────────────────────────────────
 * A saver is made when a draft is loaded into the plan document store and is disposed when that
 * document is let go or replaced. It is bound to the conversation and the build turn it was made
 * for, and it never learns another: a conversation switched to is a new saver, so nothing this one
 * still owes is ever sent to a conversation it was not made for. And it sends only the document it
 * was made for: `read` answers `null` once the store holds anything else (a seed for the builder,
 * another draft), and then there is nothing to send.
 *
 * ── What a save comes to ─────────────────────────────────────────────────────────────────────
 *   saved     the answer's revision is the next edit's base; the tree is not reloaded.
 *   failed    no answer, a timeout, a server failure: sent again after the turn watch's back-off,
 *             `MAX_ATTEMPTS` times in all, and then on the next edit or flush.
 *   busy      429: sent again after the server's own `Retry-After`, or the back-off when it names
 *             none; counted with the failures.
 *   invalid   the save's rules refuse the plan: the violations stand, and nothing is sent until the
 *             tree changes.
 *   rejected  any other 4xx: the request itself is refused, and the same tree is never sent again.
 *   stale     the draft moved on elsewhere: this saver is finished, and its host takes the server's
 *             draft.
 *   gone      404 or 403: the conversation or the draft is not there for this trainer any more.
 *             This saver is finished; the edits stay in the panel and nothing can take them.
 *
 * One save on the wire at a time: a save asked for while one is on its way waits for it, because
 * each save is made against the revision the previous one answered with.
 *
 * ── Held while a decision on the same draft is written ───────────────────────────────────────
 * A decision on an exercise moves the draft's revision on the server. Edits made while it is on its
 * way are held (`hold`), and once it lands they are sent on the revision it answered with
 * (`resume`): the server puts a picked exercise back on a slot sent without one, so the trainer's
 * edits and the decision both stand. A decision that failed resumes on the revision it left.
 *
 * ── The unload send has a size ───────────────────────────────────────────────────────────────
 * The browser refuses a `keepalive` body above 64 KB. A plan that large is not sent as the page goes
 * away; the browser's own leave prompt, which asks while anything is unsaved, is its guard.
 *
 * Owns a timer and a queue; the store, the transport and the cache arrive as dependencies.
 */

import {
  turnFromWire,
  turnPollDelayMs,
  type AiDraftEditOutcome,
  type AiDraftViolation,
} from '~/entities/ai-conversation';
import { DraftSerialisationError, draftToDraftEditRequest, type PlanDraft } from '~/entities/plan-draft';
import type {
  TrainWithMeModulesAiDtosResponsesAiTurnResponse as TurnDto,
  TrainWithMeModulesPlansDtosRequestsPostPlanTemplateRequest as PlanRequest,
} from '~/shared/api/generated/model';

/** How long the trainer has to stop editing before the draft is saved: a typed number is one save. */
export const SAVE_DEBOUNCE_MS = 700;

/** Sends of one tree that fail before the saver stops trying on its own. */
export const MAX_ATTEMPTS = 5;

/** The browser's cap on a `keepalive` request body. */
export const KEEPALIVE_MAX_BYTES = 64 * 1024;

/** What the last save of the hosted draft came to. */
export type DraftSaveStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'saved' }
  /** `retrying` is whether the saver will send again on its own. */
  | { readonly kind: 'failed'; readonly retrying: boolean }
  | { readonly kind: 'busy' }
  | {
      readonly kind: 'invalid';
      readonly violations: readonly AiDraftViolation[];
      /** The plan as it was sent: what the violations' paths point into. */
      readonly sent: PlanRequest | null;
    }
  | { readonly kind: 'rejected' }
  | { readonly kind: 'stale' }
  | { readonly kind: 'gone' };

export interface DraftSaverDeps {
  /** The hosted tree and its store revision, or `null` once the store holds another document. */
  readonly read: () => { readonly revision: number; readonly draft: PlanDraft } | null;
  readonly send: (plan: PlanRequest, draftRevision: number, keepalive: boolean) => Promise<AiDraftEditOutcome>;
  readonly onStatus: (status: DraftSaveStatus) => void;
  /** A save landed: the turn as the server holds it now. */
  readonly onSaved: (turn: TurnDto) => void;
  /** The draft moved on elsewhere; the host reads it again and takes it. */
  readonly onStale: () => void;
  /** The conversation or the draft is gone for this trainer. */
  readonly onGone: () => void;
}

export interface DraftSaver {
  /** The draft revision the hosted tree stands for. */
  readonly draftRevision: () => number;
  /** The tree changed: save it once nothing has changed for a moment. */
  readonly edited: () => void;
  /**
   * Send what is unsaved now, after the save on its way. Answers whether the server holds the tree
   * as it stands (or there is nothing of this saver's left to hold).
   */
  readonly flush: () => Promise<boolean>;
  /** The page is going away: send what is unsaved at once, as a request that outlives it. */
  readonly flushOnUnload: () => void;
  /** Whether the panel holds edits the draft does not have, which leaving would lose. */
  readonly hasUnsaved: () => boolean;
  /** A decision on this draft is on its way: nothing is sent until `resume`. */
  readonly hold: () => void;
  /**
   * The decision answered: edits still unsaved are sent on `draftRevision` (the revision the decision
   * left, or `null` when it failed and the revision did not move). Answers whether edits were
   * waiting; when none were, the host takes the server's draft instead.
   */
  readonly resume: (draftRevision: number | null) => boolean;
  readonly dispose: () => void;
}

export function createDraftSaver(
  start: { readonly draftRevision: number; readonly savedAt: number },
  deps: DraftSaverDeps,
): DraftSaver {
  let draftRevision = start.draftRevision;
  /** The store revision the server holds. */
  let savedAt = start.savedAt;
  /** The store revision the server refused; never sent again. */
  let refusedAt: number | null = null;
  /** Stale or gone: this saver sends nothing more. */
  let finished: 'stale' | 'gone' | null = null;
  let disposed = false;
  let held = false;
  let attempts = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let onTheWire: Promise<void> = Promise.resolve();

  const status = (next: DraftSaveStatus) => {
    if (!disposed) deps.onStatus(next);
  };

  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const schedule = (delayMs: number) => {
    clearTimer();
    timer = setTimeout(() => {
      timer = null;
      void queue();
    }, delayMs);
  };

  /** The tree, when it is this saver's and the server does not have it yet. */
  const owed = () => {
    if (disposed || held || finished !== null) return null;
    const tree = deps.read();
    return tree === null || tree.revision === savedAt || tree.revision === refusedAt ? null : tree;
  };

  /** The tree as the request it is saved as, or `null` when the save would refuse it before it left. */
  const request = (tree: { readonly revision: number; readonly draft: PlanDraft }): PlanRequest | null => {
    try {
      return draftToDraftEditRequest(tree.draft);
    } catch (error) {
      if (!(error instanceof DraftSerialisationError)) throw error;
      // A session with nothing in it, most often.
      refusedAt = tree.revision;
      status({ kind: 'invalid', violations: [], sent: null });
      return null;
    }
  };

  const retryLater = (delayMs: number) => {
    attempts += 1;
    if (attempts < MAX_ATTEMPTS) schedule(delayMs);
  };

  const sendOnce = async () => {
    const tree = owed();
    if (tree === null) return;
    const plan = request(tree);
    if (plan === null) return;
    status({ kind: 'saving' });
    const outcome = await deps.send(plan, draftRevision, false);
    if (disposed) return;
    if (outcome.outcome === 'saved') {
      const read = turnFromWire(outcome.turn);
      if (read.result?.kind === 'build') draftRevision = read.result.result.revision;
      savedAt = tree.revision;
      refusedAt = null;
      attempts = 0;
      deps.onSaved(outcome.turn);
      status({ kind: 'saved' });
      return;
    }
    const { refusal } = outcome;
    switch (refusal.kind) {
      case 'invalid':
        refusedAt = tree.revision;
        status({ kind: 'invalid', violations: refusal.violations, sent: plan });
        return;
      case 'rejected':
        refusedAt = tree.revision;
        status({ kind: 'rejected' });
        return;
      case 'stale':
        finished = 'stale';
        status({ kind: 'stale' });
        deps.onStale();
        return;
      case 'gone':
        finished = 'gone';
        status({ kind: 'gone' });
        deps.onGone();
        return;
      case 'tooMany':
        retryLater(
          refusal.retryAfterSeconds === null ? turnPollDelayMs(attempts + 1) : refusal.retryAfterSeconds * 1_000,
        );
        status(attempts < MAX_ATTEMPTS ? { kind: 'busy' } : { kind: 'failed', retrying: false });
        return;
      case 'failed':
        retryLater(turnPollDelayMs(attempts + 1));
        status({ kind: 'failed', retrying: attempts < MAX_ATTEMPTS });
        return;
    }
  };

  /** One save after whatever is on the wire. */
  const queue = (): Promise<void> => {
    const next = onTheWire.then(sendOnce);
    // The caller hears of a throw; the queue behind it does not inherit it, or no save would run again.
    onTheWire = next.catch(() => undefined);
    return next;
  };

  return {
    draftRevision: () => draftRevision,
    edited: () => {
      if (owed() === null) return;
      attempts = 0;
      schedule(SAVE_DEBOUNCE_MS);
    },
    flush: async () => {
      clearTimer();
      attempts = 0;
      await queue();
      // An edit that landed while the first save was on its way is the second's.
      if (owed() !== null) await queue();
      if (finished === 'stale') return false;
      const tree = disposed ? null : deps.read();
      return tree === null || tree.revision === savedAt;
    },
    flushOnUnload: () => {
      clearTimer();
      const tree = owed();
      if (tree === null) return;
      const plan = request(tree);
      if (plan === null) return;
      if (new TextEncoder().encode(JSON.stringify({ plan, revision: draftRevision })).length > KEEPALIVE_MAX_BYTES) return;
      // Nothing can wait now: sent beside a save still on its way, and its answer is nobody's.
      void deps.send(plan, draftRevision, true);
    },
    hasUnsaved: () => {
      if (disposed || finished === 'stale') return false;
      const tree = deps.read();
      return tree !== null && tree.revision !== savedAt;
    },
    hold: () => {
      held = true;
      clearTimer();
    },
    resume: (revision) => {
      held = false;
      if (disposed || finished !== null) return false;
      const tree = deps.read();
      const waiting = tree !== null && tree.revision !== savedAt;
      if (!waiting) return false;
      if (revision !== null) draftRevision = revision;
      attempts = 0;
      schedule(0);
      return true;
    },
    dispose: () => {
      disposed = true;
      clearTimer();
    },
  };
}
